import React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { PaperClipIcon } from '@heroicons/react/20/solid'
import { getQueryParamValue } from '../../../utils/general'
import { fabricMap, getDisplayLabel, getKeysByTitle, isHTMLContent, isSpecialKey, mediaMap, shouldApplyIndentFormat, shouldApplySpecialFormat, validateEasyViewKey } from '../../../utils/artDetailsUtils'
import useDivHeights from '../../../utils/useDivHeights'
import { isPlayerOrLNProduct, LNTransferDetailsGenerator } from '../../../utils/LNTransferDetailsGenerator'
import LNDetails from './lnDetails'
import { docLabelMap, WizardDocData } from './artDetails/artDetailsContent'
import { useArtDetails } from './artDetails/useArtDetails'
import BarcelonaDetails from './barcelonaDetails'
import EmblemWizardDetails from './ewDetails'
import { EmbroideryNote, hasNonEmptyNote, NotesCard } from './notesCard'
import { getBackendApiUrl } from '../../../config'
import { addAuthorizationHeader, getAdminId } from '../../../api/network'
import EditableInput from '../../../components/editableInput/editableInput'
import { WizardDocNote } from '../../../types/api-types'
import { renderValue } from '../../../components/cardDetails/valueRender'
import { greekLetters, letters, numbers } from './rosterGrid'
import { PackContentsTable } from './artDetails/packContentCard'
import Liquid3DMaterialSelectionDetails, { getLiquid3DMaterialSelections } from './liquid3dMaterialSelectionDetails'
import Liquid3DProductDetails from './liquid3dProductDetails'
import { isLiquid3DItemNumber } from '../../../utils/liquid3d'

// Styles
import styles from './info.module.css'
import NhlDetails from './nhlDetails'

interface Attachment {
  name: string;
  size: string;
  downloadLink: string;
}

export interface InfoProps {
  about?: string;
  attachments?: Attachment[];
  isLNTransfer: boolean;
  isEasyView: boolean;
  isWizardDesign: boolean;
  isBarcelona: boolean;
  isNhl: boolean;
  isEmblemWizard: boolean;
  originalDocId?: string;
  productType: string;
  fullWizardData?: any;
  liquid3DOriginalFileName?: string;
  extraDocData?: WizardDocData;
  internalNotes: WizardDocNote[];
  [key: string]: any;
}

const AttachmentList = ({ attachments }: { attachments: Attachment[] }) => (
  <ul role="list" className="divide-y divide-gray-100 rounded-md border border-gray-200">
    {attachments.map((attachment, index) => (
      <li key={index} className="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6">
        <div className="flex w-0 flex-1 items-center">
          <PaperClipIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
          <div className="ml-4 flex min-w-0 flex-1 gap-2">
            <span className="truncate font-medium">{attachment.name}</span>
            <span className="flex-shrink-0 text-gray-400">{attachment.size}</span>
          </div>
        </div>
        <div className="ml-4 flex-shrink-0">
          <a href={attachment.downloadLink} className="font-medium text-indigo-600 hover:text-indigo-500">
            Download
          </a>
        </div>
      </li>
    ))}
  </ul>
);

type ParsedExtendedDescriptionRow = {
  colour: string;
  elevation: string;
};

type ParsedExtendedDescription = {
  fields: { label: string; value: string }[];
  rows: ParsedExtendedDescriptionRow[];
};

type AlternateContact = {
  email?: string;
  name?: string;
  phone?: string;
};

const normalizeExtendedDescriptionKey = (value: string): string =>
  value.replace(/[^a-z0-9]/gi, '').toLowerCase();

const getExtendedDescriptionEntry = (value: unknown, names: string[]): unknown => {
  const normalizedNames = names.map(normalizeExtendedDescriptionKey);

  if (Array.isArray(value)) {
    const entry = value.find(item => {
      if (!item || typeof item !== 'object') return false;

      const record = item as Record<string, unknown>;
      return ['name', 'key', 'label', 'title', 'type'].some(key =>
        typeof record[key] === 'string'
        && normalizedNames.includes(normalizeExtendedDescriptionKey(record[key] as string))
      );
    }) as Record<string, unknown> | undefined;

    return entry?.value ?? entry?.description ?? entry?.text ?? entry?.content ?? entry;
  }

  if (!value || typeof value !== 'object') return undefined;

  const entry = Object.entries(value as Record<string, unknown>).find(([key]) =>
    normalizedNames.includes(normalizeExtendedDescriptionKey(key))
  );

  return entry?.[1];
};

const getExtendedDescriptionText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(getExtendedDescriptionText).filter(Boolean).join('\n');
  if (!value || typeof value !== 'object') return '';

  const record = value as Record<string, unknown>;
  for (const key of ['description', 'extendedDescription', 'text', 'content', 'html', 'value']) {
    const text = getExtendedDescriptionText(record[key]);
    if (text) return text;
  }

  return '';
};

const decodeExtendedDescriptionText = (value: string): string => {
  if (!value) return '';

  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .trim();
};

const parseExtendedDescription = (value?: string): ParsedExtendedDescription => {
  const text = decodeExtendedDescriptionText(value ?? '');
  if (!text) {
    return { fields: [], rows: [] };
  }

  const lines = text.split('\n').map(line => line.trim());
  const fields: { label: string; value: string }[] = [];
  const rows: ParsedExtendedDescriptionRow[] = [];

  let tableStarted = false;

  for (const line of lines) {
    if (!line) {
      continue;
    }

    if (/^colour\s*\|\s*elevation/i.test(line) || /^color\s*\|\s*elevation/i.test(line)) {
      tableStarted = true;
      continue;
    }

    if (tableStarted) {
      if (/^---\s*\|\s*---$/.test(line)) {
        continue;
      }

      const rowMatch = line.match(/^(.+?)\s*\|\s*(.+)$/);
      if (rowMatch) {
        rows.push({
          colour: rowMatch[1].trim(),
          elevation: rowMatch[2].trim(),
        });
      }
      continue;
    }

    const fieldMatch = line.match(/^([^:]+):\s*(.+)$/);
    if (fieldMatch) {
      fields.push({
        label: fieldMatch[1].trim(),
        value: fieldMatch[2].trim(),
      });
    }
  }

  return { fields, rows };
};

const getExtendedDescriptionValue = (description: string, labels: string[]): string | undefined => {
  const text = decodeExtendedDescriptionText(description);

  for (const label of labels) {
    const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const value = text.match(new RegExp(`^${escapedLabel}\\s*:\\s*(.+)$`, 'im'))?.[1]?.trim();
    if (value) return value;
  }

  return undefined;
};

const getAlternateContact = (value: unknown): AlternateContact | undefined => {
  if (typeof value === 'string') {
    const text = decodeExtendedDescriptionText(value);
    const email = getExtendedDescriptionValue(text, [
      'Proof Contact Email',
      'Alternate Contact Email',
      'Contact Email',
      'Email',
    ]) ?? text.match(/[\w.+-]+@[\w.-]+\.[a-z]{2,}/i)?.[0];
    const name = getExtendedDescriptionValue(text, [
      'Proof Contact Name',
      'Alternate Contact Name',
      'Contact Name',
      'Name',
    ]);
    const phone = getExtendedDescriptionValue(text, [
      'Proof Contact Phone',
      'Alternate Contact Phone',
      'Contact Phone',
      'Phone',
      'Telephone',
    ]);

    return email || name || phone ? { email, name, phone } : undefined;
  }

  if (!value || typeof value !== 'object') return undefined;

  const record = value as Record<string, unknown>;
  const getValue = (...keys: string[]) => Object.entries(record).find(([key]) =>
    keys.includes(normalizeExtendedDescriptionKey(key))
  )?.[1];
  const email = getValue('email', 'emailaddress');
  const name = getValue('name', 'contactname');
  const phone = getValue('phone', 'telephone', 'phonenumber');

  if (typeof email !== 'string' && typeof name !== 'string' && typeof phone !== 'string') return undefined;

  return {
    email: typeof email === 'string' && email.trim() ? email : undefined,
    name: typeof name === 'string' && name.trim() ? name : undefined,
    phone: typeof phone === 'string' && phone.trim() ? phone : undefined,
  };
};

export default function Info({
  attachments,
  about,
  isLNTransfer,
  isEasyView,
  isWizardDesign,
  isBarcelona,
  isNhl,
  isEmblemWizard,
  originalDocId,
  productType,
  fullWizardData,
  liquid3DOriginalFileName,
  extraDocData,
  internalNotes,
  ...restProps
}: InfoProps) {
  const liquid3DExtendedDescriptions = fullWizardData?.extendedData?.extendedDescriptions
    ?? fullWizardData?.extendedDescriptions
    ?? restProps.extendedDescriptions;
  const liquid3DExtendedDescription = [
    fullWizardData?.extendedData?.extendedDescription,
    fullWizardData?.extendedDescription,
    restProps.extendedDescription,
    liquid3DExtendedDescriptions,
    getExtendedDescriptionEntry(liquid3DExtendedDescriptions, ['selection details', 'material selection details']),
  ].map(getExtendedDescriptionText).filter(Boolean).join('\n');
  const isLiquid3D = [
    fullWizardData?.extendedData?.ItemNo,
    fullWizardData?.extendedData?.itemno_,
    fullWizardData?.ItemNo,
    fullWizardData?.itemno_,
    restProps.ItemNo,
    restProps.itemno_,
  ].some(isLiquid3DItemNumber);
  const liquid3DMaterialSelections = isLiquid3D
    ? getLiquid3DMaterialSelections(typeof liquid3DExtendedDescription === 'string' ? liquid3DExtendedDescription : '')
    : [];
  const showLNTrasnferDetails = isLNTransfer && Object.keys(LNTransferDetailsGenerator).includes(productType)
  const artIdParam = getQueryParamValue("artId");
  const currentUrl = new URL(window.location.href);
  const originalDocIsDifferent = originalDocId !== artIdParam;
  const artNotes: string = restProps['artNotes'];
  const specialInstructions: string|undefined = isEasyView ? restProps['Notes'] : undefined;
  const sewFileEmbroideryNotes = restProps['sewFileEmbroideryNotes'] // Customer Note
  const extendedDescriptionSource =
    fullWizardData?.extendedData?.extendedDescription ??
    fullWizardData?.extendedData?.extendedDescriptionHTML ??
    restProps['extendedDescription'] ??
    restProps['extendedDescriptionHTML'] ??
    liquid3DExtendedDescription;
  const extendedDescriptionText = typeof extendedDescriptionSource === 'string' ? decodeExtendedDescriptionText(extendedDescriptionSource) : '';
  const parsedExtendedDescription = useMemo(
    () => parseExtendedDescription(extendedDescriptionText),
    [extendedDescriptionText]
  );
  const isLiquid3DItem = useMemo(() => (
    isLiquid3D
    || parsedExtendedDescription.fields.some(field => field.label === 'Product Type' && field.value === 'Liquid 3D')
    || /_(?:LIQUID_3D|ARTPROOF_L3D_US)/i.test(extendedDescriptionText)
  ), [extendedDescriptionText, isLiquid3D, parsedExtendedDescription.fields]);
  const liquid3DAlternateContact = useMemo(() => getAlternateContact(
    fullWizardData?.extendedData?.alternateContact
    ?? getExtendedDescriptionEntry(liquid3DExtendedDescriptions, ['alternate contact'])
    ?? fullWizardData?.alternateContact
    ?? restProps.alternateContact
    ?? liquid3DExtendedDescription
  ), [fullWizardData, liquid3DExtendedDescription, liquid3DExtendedDescriptions, restProps.alternateContact]);
  const liquid3DSpecialInstructions = useMemo(() => getExtendedDescriptionValue(
    liquid3DExtendedDescription,
    ['Special Instructions']
  ), [liquid3DExtendedDescription]);
  const {
    sewFileInternalEmbroideryNotes: note,
    sewFileInternalEmbroideryNotesDate: date,
    sewFileInternalEmbroideryNotesBy: createdBy,
  } = restProps;
  const embroideryNote: EmbroideryNote = { note, date, createdBy };
  const displayRoster = isPlayerOrLNProduct(productType) || isBarcelona;
  const adminId = getAdminId();
  const hasInternalNotes = (hasNonEmptyNote(embroideryNote) || internalNotes.length > 0) && adminId;
  const weiDesignNotes: string|undefined = adminId && restProps['WeiDesignNotes'];

  const shouldShowPackContents = useMemo(() => {
    const isValidServiceType = fullWizardData?.serviceType === 'CustomCutLetters'
      || fullWizardData?.serviceType === 'CustomCutNumbers';
    const hasQuantities = fullWizardData?.quantities &&
      typeof fullWizardData.quantities === 'object' &&
      Object.keys(fullWizardData.quantities).length > 0;

    if (!isValidServiceType || !hasQuantities) return false;

    const validCharacters = [...letters, ...numbers.map(String), ...greekLetters];
    const quantityKeys = Object.keys(fullWizardData.quantities);
    const hasValidKeys = quantityKeys.every(key => validCharacters.includes(key));

    return hasValidKeys && quantityKeys.length > 0;
  }, [productType, fullWizardData]);

  const handleArtNameChange = (designId: string, name: string) => {
    const headers = addAuthorizationHeader({
      'Content-Type': 'application/json'
    });

    return fetch(`${getBackendApiUrl()}/design-artwork-name`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        designId,
        name
      })
    }).then(async (processResp) => {
      if (!processResp.ok) throw new Error("Error changing artwork name " + await processResp.text())
      return
    });
  }

  const [maxHeight, setMaxHeight] = useState<number>(150);

  if (originalDocId && originalDocIsDifferent) {
    const paramsToRemove = ['DocNo', 'DocLineNo', 'Page'];
    paramsToRemove.forEach(param => currentUrl.searchParams.delete(param));
    currentUrl.searchParams.set('artId', originalDocId);
  }

  restProps = Object.assign(restProps.media ? { mediaText: mediaMap[restProps.media as keyof typeof mediaMap] ?? "" } : {}, restProps);
  restProps = Object.assign(restProps.requestedFabric ? { requestedFabricText: fabricMap[restProps.requestedFabric as keyof typeof fabricMap] ?? "" } : {}, restProps);

  const { mappedCards, processedDataByCards, getFieldGroupInfo } = useArtDetails(fullWizardData, restProps, isEasyView, isLNTransfer, artIdParam);

  const processedCardsArray = useMemo(() => {
    if (!processedDataByCards || processedDataByCards.size === 0) {
      return [];
    }

    return Array.from(processedDataByCards.entries())
      .map(([cardTitle, cardData]) => ({
        title: cardTitle,
        data: cardData
      }))
      .filter(card => {
        return Object.keys(card.data).length > 0 &&
          Object.values(card.data).some(value => {
            if (value === null || value === undefined) return false;
            if (typeof value === 'string') return value.trim() !== '';
            return true;
          });
      });
  }, [processedDataByCards]);

  const displayedCardsArray = useMemo(() => {
    if (!isLiquid3D) return processedCardsArray;

    return processedCardsArray
      .filter(card => card.title !== 'MATERIAL SELECTION DETAILS' && card.title !== 'PRODUCT DETAILS');
  }, [isLiquid3D, processedCardsArray]);

  const liquid3DCardCount = isLiquid3D ? 1 + (liquid3DMaterialSelections.length > 0 ? 1 : 0) : 0;
  const { refs, heights } = useDivHeights(displayedCardsArray.length + liquid3DCardCount + (shouldShowPackContents ? 1 : 0));

  const productDetailsKeys = getKeysByTitle(displayedCardsArray, "PRODUCT DETAILS");

  useEffect(() => {
    const max = Math.max(...heights);
    setMaxHeight(max);
  }, [heights]);

  function checkMappedCards(): string {
    const hasNotes = Boolean(artNotes || hasInternalNotes || sewFileEmbroideryNotes || specialInstructions || weiDesignNotes);
    const numMappedCards = displayedCardsArray.length + (isLiquid3D ? 1 : 0);
    const packContentsCard = shouldShowPackContents ? 1 : 0;
    const materialSelectionsCard = liquid3DMaterialSelections.length > 0 ? 1 : 0;

    const totalColumns = (showLNTrasnferDetails || isBarcelona)
      ? (displayRoster ? 2 : 1) + (hasNotes ? 1 : 0)
      : numMappedCards + packContentsCard + materialSelectionsCard + (hasNotes ? 1 : 0);

    const columnStyles: Record<number, string> = {
      5: styles.fiveColumns,
      4: styles.fourColumns,
      3: styles.threeColumns,
      2: styles.twoColumns,
      1: styles.oneColumn
    };

    return columnStyles[totalColumns] || styles.oneColumn;
  }

  const getFieldLabel = (key: string) => {
    for (const card of mappedCards) {
      const property = card.properties.find(prop => prop.key === key);
      if (property) return property.label;
    }

    for (const card of mappedCards) {
      const property = card.properties.find(prop => prop.key === key);
      if (property) return property.label;
    }

    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const hiddenDetailKeys = new Set([
    'extendedDescription',
    'extendedDescriptionHTML',
    'extendedDescriptionHtml',
    ...(isLiquid3DItem ? [
      'media',
      'mediaText',
      'media_original',
      'itemNo',
      'ItemNo',
      'documentId',
      'DocumentId',
    ] : []),
  ]);

  return (
    <>
      <div className={styles.infoWrapper}>
        {/* L&N Design Details */}
        {showLNTrasnferDetails && (
          <LNDetails
            productType={productType}
            fullWizardData={fullWizardData}
            displayRoster={displayRoster}
            checkMappedCards={checkMappedCards} />
        )}

        {/* Barcelona Design Details */}
        {isBarcelona && (
          <BarcelonaDetails
            fullWizardData={fullWizardData}
            displayRoster={displayRoster}
            checkMappedCards={checkMappedCards} />
        )}

        {/* Nhl Design Details */}
        {isNhl && (
          <NhlDetails
            fullWizardData={fullWizardData} />
        )}

        {/* Emblem wizard Design Details */}
        {isEmblemWizard &&
          <EmblemWizardDetails
            wizardData={fullWizardData}
          />
        }

        {/* Art Details */}
        {!showLNTrasnferDetails && !isBarcelona && !isNhl && !isEmblemWizard &&
          displayedCardsArray.map((cardData, index) => (
            <div key={index} className={[styles.card, checkMappedCards()].join(' ')}>
              <h3 className={styles.title}>{cardData.title}</h3>
              <div className={styles.cardBody}
                ref={el => (refs.current[index] = el!)}>

                {originalDocId && index == 0 && originalDocIsDifferent && (
                  <div className={styles.details} key={"originalDocId"}>
                    <span className={styles.name}>Original Doc:</span>
                    <span className={styles.value}>
                      <a href={currentUrl.toString()} className="font-medium text-indigo-600 hover:text-indigo-500">
                        View Original Document
                      </a>
                    </span>
                  </div>
                )}

                {isLiquid3D && cardData.title === 'ART DETAILS' && liquid3DAlternateContact && (
                  <>
                    {liquid3DAlternateContact.name && (
                      <div className={styles.details}>
                        <span className={styles.name}>Contact Name:</span>
                        <span className={styles.value}>{liquid3DAlternateContact.name}</span>
                      </div>
                    )}
                    {liquid3DAlternateContact.email && (
                      <div className={styles.details}>
                        <span className={styles.name}>Contact Email:</span>
                        <span className={styles.value}>{liquid3DAlternateContact.email}</span>
                      </div>
                    )}
                    {liquid3DAlternateContact.phone && (
                      <div className={styles.details}>
                        <span className={styles.name}>Contact Phone:</span>
                        <span className={styles.value}>{liquid3DAlternateContact.phone}</span>
                      </div>
                    )}
                  </>
                )}

                {isLiquid3D && cardData.title === 'ART DETAILS' && liquid3DSpecialInstructions && (
                  <div className={styles.details}>
                    <span className={styles.name}>Special Instructions:</span>
                    <span className={styles.value}>{liquid3DSpecialInstructions}</span>
                  </div>
                )}

                {Object.entries(cardData.data).map(([key, value]) => {
                  if (hiddenDetailKeys.has(key)) return null;
                  if (isLiquid3D && key === 'origFileName') return null;

                  const displayValue = renderValue(value);
                  if (displayValue === null) return null;

                  const fieldLabel = getFieldLabel(key);
                  if (isEasyView && !validateEasyViewKey(key)) return null;

                  const allKeys: string[] = Object.keys(cardData.data);
                  const groupInfo = getFieldGroupInfo(key, cardData.title, allKeys, productDetailsKeys);

                  const shouldIndent =
                    (cardData.title?.toLowerCase() === 'material selection details'
                      && !isEasyView
                      && isSpecialKey(key)
                      && !shouldApplySpecialFormat(key, productType))
                    || shouldApplyIndentFormat(key, productType);

                  const displayLabel = getDisplayLabel(key, fieldLabel, productType);
                  const digitalColorHex = String(cardData.data.digitalColorHex ?? '').trim().replace(/^#/, '');
                  const showDigitalColorSwatch = key === 'digitalColorName'
                    && fullWizardData?.productType === 'DecalsPack'
                    && fullWizardData?.productSubType === 'DecalPacks'
                    && /^(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(digitalColorHex);


                  return (
                    <React.Fragment key={key}>
                      {(groupInfo.isGroupStart
                        && groupInfo.groups[0].name === groupInfo.groupName)
                        && <div className={styles.groupSpacer} />
                      }

                      <div
                        className={styles.details}
                        data-field={key}
                        style={{ marginLeft: shouldIndent ? '12px' : '0px' }}
                      >
                        <span className={styles.name}>
                          {key === 'specialInstructions'
                            ? 'Special Instructions'
                            : key === 'quantities' && typeof value === 'object'
                            ? 'Total Quantity'
                            : displayLabel
                          }:
                        </span>
                        {fieldLabel === 'Artwork Name' ?
                          <span className={styles.value}>
                            <EditableInput
                              text={value as string}
                              onChange={changedText => handleArtNameChange(artIdParam ?? "", changedText)}
                            />
                          </span> :
                          showDigitalColorSwatch ?
                            <span className={styles.value}>
                              <span
                                className="color-swatch"
                                aria-hidden="true"
                                style={{ backgroundColor: `#${digitalColorHex}`, verticalAlign: 'middle', marginRight: '5px' }}
                              />
                              {displayValue}
                            </span> :
                          isHTMLContent(displayValue.props?.value) ?
                            <span
                              className={styles.value}
                              dangerouslySetInnerHTML={{ __html: displayValue.props?.value }}
                            /> :
                            <span className={styles.value}>
                              {key === 'aspectRatio' && typeof value === 'number' && !isNaN(value) && isFinite(value)
                                ? value.toFixed(2)
                                : key === 'quantities' && value !== null && typeof value === 'object'
                                  ? Object.values(value).reduce((sum: number, v) => sum + (Number(v) || 0), 0).toString()
                                  : displayValue
                              }
                            </span>
                        }
                      </div>

                      {groupInfo.isGroupEnd && (
                        <div className={styles.groupSpacer} />
                      )}
                    </React.Fragment>
                  );
                })}

                {!isLiquid3D && cardData.title === 'PRODUCT DETAILS' && parsedExtendedDescription.fields.length > 0 && (
                  <>
                    {parsedExtendedDescription.fields.map((field, fieldIndex) => (
                      <div className={styles.details} key={`extended-description-field-${fieldIndex}`}>
                        <span className={styles.name}>{field.label}:</span>
                        <span className={styles.value}>{field.value}</span>
                      </div>
                    ))}

                    {parsedExtendedDescription.rows.length > 0 && (
                      <div className={styles.details} key="extended-description-table">
                        <div className="w-full overflow-hidden rounded-md border border-gray-200">
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="border-b border-gray-200 text-left">
                                <th className="py-2 pr-4 font-semibold text-gray-700">Colour</th>
                                <th className="py-2 font-semibold text-gray-700">Elevation (mm)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {parsedExtendedDescription.rows.map((row, rowIndex) => (
                                <tr key={`${row.colour}-${rowIndex}`} className="border-b border-gray-100 last:border-b-0">
                                  <td className="py-2 pr-4 text-gray-900">{row.colour}</td>
                                  <td className="py-2 text-gray-900">{row.elevation}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {isLiquid3D && cardData.title === 'ART DETAILS' && liquid3DOriginalFileName && (
                  <div className={styles.details}>
                    <span className={styles.name}>Original File Name:</span>
                    <span className={styles.value}>{liquid3DOriginalFileName}</span>
                  </div>
                )}

                {/* Render doc data */}
                {extraDocData && index == 0 &&
                  Object.entries(extraDocData).map(([key, value]) => (
                    <div className={styles.details} key={key}>
                      <span className={styles.name}>{docLabelMap[key as keyof WizardDocData]}:</span>
                      <span className={styles.value}>{value}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          ))}

        {isLiquid3D && (
          <Liquid3DProductDetails
            description={typeof liquid3DExtendedDescription === 'string' ? liquid3DExtendedDescription : ''}
            columnClassName={checkMappedCards()}
            bodyRef={el => (refs.current[displayedCardsArray.length] = el!)}
          />
        )}

        {isLiquid3D && liquid3DMaterialSelections.length > 0 && (
          <Liquid3DMaterialSelectionDetails
            description={typeof liquid3DExtendedDescription === 'string' ? liquid3DExtendedDescription : ''}
            columnClassName={checkMappedCards()}
            bodyRef={el => (refs.current[displayedCardsArray.length + 1] = el!)}
          />
        )}

        {/* Pack Contents Card */}
        {shouldShowPackContents && (
          <div className={[styles.card, checkMappedCards()].join(' ')}>
            <PackContentsTable quantities={fullWizardData.quantities} />
          </div>
        )}

        {/* Art Notes Column */}
        {(artNotes || hasInternalNotes || sewFileEmbroideryNotes || specialInstructions || weiDesignNotes) &&
          (<div className={[styles.card, checkMappedCards(), showLNTrasnferDetails ? styles.bgWhite : ""].join(' ')}>
            <h3 className={styles.title}>NOTES</h3>
            <NotesCard
              artNotes={artNotes}
              embroideryNotes={embroideryNote}
              customerEmbroideryNote={sewFileEmbroideryNotes}
              internalNotes={internalNotes}
              specialInstructions={specialInstructions}
              maxHeight={maxHeight}
              weiDesignNotes={weiDesignNotes}
            />
          </div>
          )}
      </div>
      {/* Attachments */}
      <div>
        {attachments && attachments.length > 0 && (
          <div className="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
            <dt className="text-sm font-medium leading-6 text-gray-900">Attachments</dt>
            <dd className="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
              <AttachmentList attachments={attachments} />
            </dd>
          </div>
        )}
      </div>
    </>
  );
}